import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FaPlus,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaTimes,
} from "react-icons/fa";
import { FaFilterCircleXmark, FaArrowDownShortWide, FaArrowUpShortWide } from "react-icons/fa6";

import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import LoadingOverlay from "../../components/loadingOverlay";

import StatusBadge from "../../components/activities/statusBadge";
import TypeBadge from "../../components/activities/typeBadge";
import RankingBadge from "../../components/activities/rankingBadge";

import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../config/apiConfig";
import {
  resetActivities,
  storeActivitiesPage,
} from "../../features/activities/activitiesSlice";

export default function ManageActivitiesPage() {
  const token = sessionStorage.getItem("access_token");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cache = useSelector((s) => s.activities.cache);
  const totalMap = useSelector((s) => s.activities.total);

  const [page, setPage] = useState(1);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [rankingTypeFilter, setRankingTypeFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("latest");

  // Searching
  const [searching, setSearching] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const filterKey = `${statusFilter}|${typeFilter}|${rankingTypeFilter}|${sortOrder}`;

  // ----------------------------------------------------------------------
  // LOAD PAGE (Server-side + caching)
  // ----------------------------------------------------------------------
  useEffect(() => {
    if (!searching) {
      setPage(1);
      dispatch(resetActivities());
      loadPage(1);
    }
  }, [statusFilter, typeFilter, rankingTypeFilter, sortOrder]);

  async function loadPage(pageNumber) {
    // Cached
    if (cache[filterKey] && cache[filterKey][pageNumber]) {
      setActivities(cache[filterKey][pageNumber]);
      return;
    }

    setLoading(true);
    try {
      const url = new URL(`${API_BASE}/api/activities-list`);
      url.searchParams.append("page", pageNumber);
      url.searchParams.append("limit", 8);
      url.searchParams.append("status", statusFilter);
      url.searchParams.append("type", typeFilter);
      url.searchParams.append("rankingType", rankingTypeFilter);
      url.searchParams.append("sort", sortOrder);
      url.searchParams.append("visibility", "public");

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (data.success) {
        setActivities(data.activities);

        dispatch(
          storeActivitiesPage({
            key: filterKey,
            page: pageNumber,
            items: data.activities,
            total: data.total,
          })
        );
      }
    } finally {
      setLoading(false);
    }
  }

  // ----------------------------------------------------------------------
  // SEARCH MODE
  // ----------------------------------------------------------------------
  async function handleSearch() {
    const q = searchText.trim();
    if (q.length < 3) return;

    setSearching(true);
    setLoading(true);

    try {
      const url = new URL(`${API_BASE}/api/activities-search`);
      url.searchParams.append("query", q);
      url.searchParams.append("visibility", "public");

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (data.success) {
        setSearchResults(data.results);
      }
    } finally {
      setLoading(false);
    }
  }

  function clearSearch() {
    setSearchText("");
    setSearching(false);
    setSearchResults([]);
    setPage(1);
    loadPage(1);
  }

  // ----------------------------------------------------------------------
  // PAGINATION
  // ----------------------------------------------------------------------
  const total = totalMap[filterKey] || 0;
  const totalPages = Math.ceil(total / 8);

  function goNext() {
    if (searching) return;
    if (page < totalPages) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadPage(nextPage);
    }
  }

  function goPrev() {
    if (searching) return;
    if (page > 1) {
      const prevPage = page - 1;
      setPage(prevPage);
      loadPage(prevPage);
    }
  }

  // ----------------------------------------------------------------------
  // RESET FILTERS
  // ----------------------------------------------------------------------
  function resetFilterOptions() {
    setStatusFilter("All");
    setTypeFilter("All");
    setRankingTypeFilter("All");
  }

  // ----------------------------------------------------------------------
  // RENDER ITEMS
  // ----------------------------------------------------------------------
  function renderActivityItem(act, idx) {
    return (
      <li
        key={act.id}
        onClick={() => navigate(`/activities/${act.id}`)}
        className="p-2 flex flex-col gap-1 hover:bg-gray-50 cursor-pointer"
      >
        {/* TOP: index + name */}
        <div className="flex items-start gap-1 text-left">
          <span className="text-gray-500 shrink-0">
            {idx + 1 + (!searching ? (page - 1) * 8 : 0)}.
          </span>

          <span className="text-blue-600 underline hyphens-auto">
            {act.name}
          </span>
        </div>
        
        {/* BOTTOM: badges */}
        <div className="flex flex-wrap gap-1 justify-end">
          <StatusBadge status={act.status} />
          <TypeBadge type={act.type} />
          {act.type === "tournament" && <RankingBadge ranking={act.rankingType} />}
        </div>
      </li>
    );
  }

  return (
    <MainContentLayout>
      <LoadingOverlay show={loading} />
      <PageHeader title="Manage Activities" backTo="/dashboard" />
      {/* ----------------------------- FILTER ROW ----------------------------- */}
      <div className={`flex flex-col w-full gap-2 ${searchText.length > 0 ? "opacity-40 pointer-events-none" : ""}`}>
        {/* ROW 1 — filters + add button */}
        <div className="flex items-center justify-between w-full">
          {/* Left side filters */}
          <div className="flex flex-wrap gap-3 flex-grow">
            {/* Status */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="All">All Status</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
              <option value="upcoming">Upcoming</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              <option value="draft">Draft</option>
              <option value="full">Full</option>
              <option value="closed">Closed</option>
            </select>

            {/* Type */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="border rounded px-2 py-1"
            >
              <option value="All">All Types</option>
              <option value="event">Event</option>
              <option value="tournament">Tournament</option>
              <option value="camp">Camp</option>
              <option value="seminar">Seminar</option>
              <option value="workshop">Workshop</option>
              <option value="talk">Talk</option>
              <option value="sparring">Sparring</option>
              <option value="fitness">Fitness</option>
              <option value="friendlyMatch">Friendly Match</option>
            </select>

            {/* Ranking type */}
            {typeFilter === "tournament" && (
              <select
                value={rankingTypeFilter}
                onChange={(e) => setRankingTypeFilter(e.target.value)}
                className="border rounded px-2 py-1"
              >
                <option value="All">All Rankings</option>
                <option value="AJSS">AJSS</option>
                <option value="National">National</option>
              </select>
            )}
          </div>

          {/* Add Activity */}
          <button
            onClick={() => navigate("/activities/new")}
            className="p-3 bg-green-600 text-white rounded-full hover:bg-green-700"
          >
            <FaPlus />
          </button>
        </div>

        {/* ROW 2 — reset + sort */}
        <div className="flex gap-3">
          {/* Reset Filters */}
          <button
            onClick={resetFilterOptions}
            className="p-2 text-gray-600 hover:text-black"
            title="Reset Filters"
          >
            <FaFilterCircleXmark size={18} />
          </button>
          <button
            onClick={() => {
              const newSort = sortOrder === "latest" ? "oldest" : "latest";
              setSortOrder(newSort);
              setPage(1);
              dispatch(resetActivities());
            }}
            className="p-2 bg-gray-200 rounded"
          >
            {sortOrder === "latest"
            ? <FaArrowDownShortWide size={18} />
            : <FaArrowUpShortWide size={18} />}
          </button>
        </div>
      </div>

      {/* ----------------------------- SEARCH BAR ----------------------------- */}
      <div className="flex items-center gap-2 mt-2">
        <div className="relative flex-grow">
          <input
            type="text"
            className="w-full border rounded-lg p-3 pr-10 placeholder:italic placeholder:text-gray-400"
            placeholder="Search activities (min 3 characters)..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          {/* Clear Search (X) */}
          {searchText.length > 0 && (
            <button
              className="absolute right-10 top-1/2 -translate-y-1/2 text-gray-500"
              onClick={clearSearch}
            >
              <FaTimes />
            </button>
          )}
        </div>

        {/* Search Button */}
        <button
          onClick={handleSearch}
          disabled={searchText.trim().length < 3}
          className="p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FaSearch />
        </button>
      </div>

      {/* ----------------------------- SEARCH MODE ----------------------------- */}
      {searching ? (
        <div>
          {searchResults.length === 0 ? (
            <p className="text-gray-500">No matching activities.</p>
          ) : (
            <ul className="divide-y rounded-lg bg-white shadow-sm">
              {searchResults.map((act, i) => renderActivityItem(act, i))}
            </ul>
          )}
        </div>
      ) : (
        <>
          {/* ----------------------------- NORMAL LIST MODE ----------------------------- */}
          {activities.length === 0 ? (
            <p className="text-gray-500">No activities found.</p>
          ) : (
            <ul className="divide-y rounded-lg bg-white shadow-sm">
              {activities.map((act, i) => renderActivityItem(act, i))}
            </ul>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-6 mt-4">

              <button
                disabled={page === 1}
                onClick={goPrev}
                className="disabled:opacity-30"
              >
                <FaChevronLeft size={18} />
              </button>

              <span className="text-gray-700">
                Page {page} / {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={goNext}
                className="disabled:opacity-30"
              >
                <FaChevronRight size={18} />
              </button>

            </div>
          )}
        </>
      )}
    </MainContentLayout>
  );
}
